/* Notification Center — Trigger forms from Default Settings (demo).
 *
 * The New Rule wizard's TRIGGER step (step 2) asks WHICH application form/event
 * evaluates and fires the rule.  The available options are NOT hardcoded as
 * always-on: the running app keeps the workflow/form enablement in Default
 * Settings (Settings ▸ Default Settings — the workflow-configuration matrix),
 * and only the forms enabled there may be chosen as a trigger.
 *
 * This module is the demo stand-in for that Default Settings config, mirroring
 * the shape the real reader will return (id · label · enabled).  When the real
 * config reader lands it replaces NC_TRIGGER_FORMS; the wizard and Rule Detail
 * consume `enabledTriggerForms()` / `triggerFormById()` either way, so the
 * enabled/disabled filtering never has to change.
 *
 * The six forms are the two result-lifecycle pipelines:
 *   SDMS pipeline    → SDMS · SDMS Validation · SDMS Approval
 *   Result pipeline  → Result Entry Express · Result Validation · Result Approval
 */

/** One workflow form/event that can trigger a rule, with its Default-Settings
 *  enablement.  `enabled=false` means the lab has turned that workflow off, so
 *  it must not appear as a NEW trigger option (but a rule already saved against
 *  it is preserved — see triggerFormById / the wizard + Rule Detail). */
export interface TriggerForm {
  /** Stable key saved on the rule (an entry in NcRule.triggers[]). */
  id: string;
  /** Display label shown on the card / review / detail. */
  label: string;
  /** One-line description of the workflow event. */
  sub: string;
  /** Tabler icon. */
  icon: string;
  /** SDMS pipeline vs Result pipeline — drives the evaluation wording. */
  pipeline: 'sdms' | 'result';
  /** Enabled in Default Settings — only enabled forms are offered as triggers. */
  enabled: boolean;
}

/**
 * Demo Default-Settings workflow enablement.  SDMS Validation is OFF to show
 * the filtering (it must not appear in the wizard) while a rule already saved
 * against it is still handled safely.
 */
export const NC_TRIGGER_FORMS: TriggerForm[] = [
  { id: 'sdms',            label: 'SDMS',                 sub: 'SDMS Data package - Result Entry', icon: 'ti-file-import',  pipeline: 'sdms',   enabled: true },
  { id: 'sdms-validation', label: 'SDMS Validation',      sub: 'SDMS data review / validation',  icon: 'ti-checkup-list', pipeline: 'sdms',   enabled: false },
  { id: 'sdms-approval',   label: 'SDMS Approval',        sub: 'SDMS data approval',             icon: 'ti-circle-check', pipeline: 'sdms',   enabled: true },
  { id: 'result-entry',    label: 'Result Entry Express', sub: 'Manual result entry (REE) — on Submit', icon: 'ti-forms',        pipeline: 'result', enabled: true },
  { id: 'result-validation', label: 'Result Validation',  sub: 'Result validation event',        icon: 'ti-checks',       pipeline: 'result', enabled: true },
  { id: 'result-approval', label: 'Result Approval',      sub: 'Result approval event',          icon: 'ti-writing-sign', pipeline: 'result', enabled: true },
];

/** The trigger forms enabled in Default Settings — the options the wizard shows.
 *  Order preserved (SDMS pipeline, then Result pipeline). */
export function enabledTriggerForms(cfg: TriggerForm[] = NC_TRIGGER_FORMS): TriggerForm[] {
  return cfg.filter((f) => f.enabled);
}

/** Look up a trigger form by id — INCLUDING disabled ones, so a rule saved
 *  against a form later turned off in Default Settings still resolves its label
 *  and is never silently dropped. */
export function triggerFormById(id: string | undefined | null, cfg: TriggerForm[] = NC_TRIGGER_FORMS): TriggerForm | undefined {
  if (!id) return undefined;
  return cfg.find((f) => f.id === id);
}

/** Labels for a set of trigger ids — resolves disabled/removed ids too (so a
 *  saved-but-disabled trigger still shows its name), skipping only unknowns. */
export function triggerLabels(ids: string[] | undefined | null, cfg: TriggerForm[] = NC_TRIGGER_FORMS): string[] {
  if (!ids?.length) return [];
  return ids.map((id) => triggerFormById(id, cfg)?.label ?? id);
}

/** Human evaluation sentence for the Trigger Condition surface across the set of
 *  selected trigger forms, e.g. "Evaluated when the applicable record reaches
 *  any of: SDMS, Result Validation." — the rule fires ONCE per record even if it
 *  passes several of them (duplicate firing is suppressed). Falls back gracefully
 *  when nothing is selected. */
export function triggersEvaluationText(ids: string[] | undefined | null, cfg: TriggerForm[] = NC_TRIGGER_FORMS): string {
  const labels = triggerLabels(ids, cfg);
  if (labels.length === 0) return 'No trigger selected — this rule will not evaluate until at least one trigger form is enabled.';
  if (labels.length === 1) return `Evaluated when the applicable record reaches the ${labels[0]} event.`;
  return `Evaluated when the applicable record reaches any of: ${labels.join(', ')} (once per record — duplicates suppressed).`;
}
